export const buildSelectedStructure = (selectedPreference, fullPreference, isPrice = false) => {
  const valueMap = {};

  const traverse = (groups, parentValueId = null) => {
    (groups || []).forEach((group) => {
      (group.preferenceValues || []).forEach((value) => {
        valueMap[value.preferenceSku] = {
          preferenceId: group.preferenceId,
          preferenceValueId: value.preferenceSku,
          parentValueId,
          price: value.price ?? null,
          isLastleaf: value?.subPreferences?.length > 0 ? false : true
        };

        traverse(value.subPreferences || [], value.preferenceSku);
      });
    });
  };

  traverse(fullPreference);

  const getChain = (sku) => {
    const chain = [];

    let current = valueMap[sku];

    while (current) {
      chain.unshift(current);

      current = current.parentValueId
        ? valueMap[current.parentValueId]
        : null;
    }

    return chain;
  };

  const mergeChain = (target, chain, quantityMap) => {
    if (!chain.length) return;

    const [current, ...rest] = chain;

    let pref = target.find(
      (p) => p.preferenceId === current.preferenceId
    );

    if (!pref) {
      pref = {
        preferenceId: current.preferenceId,
        values: [],
      };
      target.push(pref);
    }

    let value = pref.values.find(
      (v) =>
        v.preferenceValueId === current.preferenceValueId
    );

    if (!value) {
      value = {
        preferenceValueId: current.preferenceValueId,
      };

      if (current?.isLastleaf && current?.price && isPrice) {
        value.price = current.price ?? null;
      }

      if (quantityMap[current.preferenceValueId] !== undefined) {
        value.quantity = quantityMap[current.preferenceValueId];
      }

      pref.values.push(value);
    }

    if (rest.length) {
      if (!value.subPreferences) {
        value.subPreferences = [];
      }

      mergeChain(value.subPreferences, rest, quantityMap);
    }
  };

  const selectedValueIds = [];
  const quantityMap = {};

  const collectSelected = (items) => {
    (items || []).forEach((item) => {
      (item.values || []).forEach((value) => {
        selectedValueIds.push(value.preferenceValueId);

        if (value.quantity !== undefined) {
          quantityMap[value.preferenceValueId] = value.quantity;
        }

        if (value.subPreferences) {
          collectSelected(value.subPreferences);
        }
      });
    });
  };

  collectSelected(selectedPreference);
  const result = [];

  selectedValueIds.forEach((sku) => {
    const chain = getChain(sku);

    if (chain.length) {
      mergeChain(result, chain, quantityMap);
    }
  });

  return result;
};

export const buildPreferencesStructure = (
  selectedPreference,
  fullPreference
) => {
  const preferenceObj = {};
  const groupsByType = {};

  const mapNodes = (
    items,
    parentSku = null,
    parentGroupPreferenceId = null
  ) => {
    items.forEach((item) => {
      preferenceObj[item.preferenceSku] = {
        sku: item.preferenceSku,
        parentSku,
        parentGroupPreferenceId,
      };

      item.subPreferences?.forEach((group) => {
        groupsByType[group.type] = group;

        mapNodes(
          group.preferenceValues || [],
          item.preferenceSku,
          group.preferenceId
        );
      });
    });
  };

  fullPreference.forEach((rootGroup) => {
    groupsByType[rootGroup.type] = rootGroup;

    mapNodes(
      rootGroup.preferenceValues || [],
      null,
      rootGroup.preferenceId
    );
  });

  const getParentPath = (sku) => {
    const chain = [];
    let node = preferenceObj[sku];

    while (node) {
      chain.unshift({
        sku: node.sku,
        groupPreferenceId: node.parentGroupPreferenceId,
      });

      node = node.parentSku
        ? preferenceObj[node.parentSku]
        : null;
    }

    return chain;
  };

  const collectSelectedSkus = (items, result = [], quantityMap = {}) => {
    items.forEach((item) => {
      if (item.preferenceSku) {
        result.push(item.preferenceSku);
        if (item.quantity !== undefined) {
          quantityMap[item.preferenceSku] = item.quantity;
        }
      }

      item.subSelections?.forEach((sub) => {
        collectSelectedSkus(sub.selection || [], result, quantityMap);
      });
    });

    return result;
  };

  const buildValues = (chains, depth, quantityMap) => {
    const grouped = {};

    chains.forEach((chain) => {
      if (chain.length <= depth) return;

      const sku = chain[depth].sku;

      if (!grouped[sku]) {
        grouped[sku] = [];
      }

      grouped[sku].push(chain);
    });

    return Object.entries(grouped).map(
      ([sku, groupedChains]) => {
        const value = {
          preferenceValueId: sku,
          quantity: quantityMap[sku] ?? 1,
        };

        const subGroups = {};

        groupedChains.forEach((chain) => {
          if (chain.length <= depth + 1) return;

          const nextGroupId =
            chain[depth + 1].groupPreferenceId;

          if (!nextGroupId) return;

          if (!subGroups[nextGroupId]) {
            subGroups[nextGroupId] = [];
          }

          subGroups[nextGroupId].push(chain);
        });

        const subPreferences = Object.entries(
          subGroups
        ).map(([groupId, chains]) => ({
          preferenceId: groupId,
          values: buildValues(chains, depth + 1, quantityMap),
        }));

        if (subPreferences.length > 0) {
          value.subPreferences = subPreferences;
        }

        return value;
      }
    );
  };

  return Object.entries(selectedPreference)
    .filter(
      ([, items]) =>
        Array.isArray(items) && items.length > 0
    )
    .map(([groupType, items]) => {
      const rootGroup = groupsByType[groupType];

      if (!rootGroup) return null;

      const quantityMap = {};
      const selectedSkus = collectSelectedSkus(items, [], quantityMap);

      const chains = selectedSkus
        .map((sku) => getParentPath(sku))
        .filter((chain) => chain.length > 0);

      return {
        preferenceId: rootGroup.preferenceId,
        values: buildValues(chains, 0, quantityMap),
      };
    })
    .filter(Boolean);
};

export const flattenSelections = (items = []) => {
  const flattened = [];

  const traverse = (node) => {
    if (!node) return;

    flattened.push(node);

    (node.subSelections || []).forEach((group) => {
      (group.selection || []).forEach((child) => {
        traverse(child);
      });
    });
  };

  items.forEach(traverse);

  return flattened;
};

export const getPreferencesLength = (items) => {
  const result = [];

  const traverse = (node) => {
    const hasChildren =
      node?.subSelections?.some(
        (group) => group?.selection?.length > 0
      );

    if (!hasChildren) {
      result.push(node);
      return;
    }

    (node.subSelections || []).forEach((group) => {
      (group.selection || []).forEach((child) => {
        traverse(child);
      });
    });
  };

  const list = Array.isArray(items)
    ? items
    : items
      ? [items]
      : [];


  list.forEach(traverse);

  return result;
};

export const convertSelectedPreference = (preferences) => {
  const result = {};

  const traverse = (pref) => {
    const items = [];

    pref.values?.forEach((value) => {
      const item = {
        preferenceSku: value.preferenceValueId,
        subSelections: [],
      };

      value.subPreferences?.forEach((subPref) => {
        item.subSelections.push({
          selection: traverse(subPref),
        });
      });

      items.push(item);
    });

    return items;
  };

  preferences.forEach((pref) => {
    result[pref.preferenceId] = traverse(pref);
  });

  return result;
};

export const buildPreferencesStructures = (
  selectedPreference,
  fullPreference
) => {
  const preferenceObj = {};
  const groupsByType = {};

  const mapNodes = (
    items,
    parentSku = null,
    parentGroupPreferenceId = null
  ) => {
    items.forEach((item) => {
      preferenceObj[item.preferenceSku] = {
        sku: item.preferenceSku,
        parentSku,
        parentGroupPreferenceId,
      };

      item.subPreferences?.forEach((group) => {
        groupsByType[group.preferenceId] = group;

        mapNodes(
          group.preferenceValues || [],
          item.preferenceSku,
          group.preferenceId
        );
      });
    });
  };

  fullPreference.forEach((rootGroup) => {
    groupsByType[rootGroup.preferenceId] = rootGroup;

    mapNodes(
      rootGroup.preferenceValues || [],
      null,
      rootGroup.preferenceId
    );
  });

  const getParentPath = (sku) => {
    const chain = [];
    let node = preferenceObj[sku];

    while (node) {
      chain.unshift({
        sku: node.sku,
        groupPreferenceId: node.parentGroupPreferenceId,
      });

      node = node.parentSku
        ? preferenceObj[node.parentSku]
        : null;
    }

    return chain;
  };

  const collectSelectedSkus = (groups, result = []) => {
    groups.forEach((group) => {
      group.values?.forEach((value) => {
        result.push(value.preferenceValueId);

        if (value.subPreferences?.length) {
          collectSelectedSkus(value.subPreferences, result);
        }
      });
    });

    return result;
  };

  const buildValues = (chains, depth) => {
    const grouped = {};

    chains.forEach((chain) => {
      if (chain.length <= depth) return;

      const sku = chain[depth].sku;

      if (!grouped[sku]) {
        grouped[sku] = [];
      }

      grouped[sku].push(chain);
    });

    return Object.entries(grouped).map(([sku, groupedChains]) => {
      const value = {
        preferenceValueId: sku,
      };

      const subGroups = {};

      groupedChains.forEach((chain) => {
        if (chain.length <= depth + 1) return;

        const nextGroupId = chain[depth + 1].groupPreferenceId;

        if (!nextGroupId) return;

        if (!subGroups[nextGroupId]) {
          subGroups[nextGroupId] = [];
        }

        subGroups[nextGroupId].push(chain);
      });

      const subPreferences = Object.entries(subGroups).map(
        ([groupId, chains]) => ({
          preferenceId: groupId,
          values: buildValues(chains, depth + 1),
        })
      );

      if (subPreferences.length) {
        value.subPreferences = subPreferences;
      }

      return value;
    });
  };

  return selectedPreference
    .map((group) => {
      const rootGroup = groupsByType[group.preferenceId];

      if (!rootGroup) return null;

      const selectedSkus = collectSelectedSkus([group]);

      const chains = selectedSkus
        .map((sku) => getParentPath(sku))
        .filter((chain) => chain.length > 0);

      return {
        preferenceId: rootGroup.preferenceId,
        values: buildValues(chains, 0),
      };
    })
    .filter(Boolean);
};

export const buildSelectedCustomizationStructure = (
  preferences,
  fullPreferenceTree
) => {
  if (!Array.isArray(preferences) || !Array.isArray(fullPreferenceTree)) {
    return [];
  }

  const buildSelection = (group, values) => {
    return values
      .map((value) => {
        const item = (group.preferenceValues || []).find(
          (p) =>
            String(p.preferenceSku) === String(value.preferenceValueId)
        );

        if (!item) return null;

        const output = {
          preferenceSku: item.preferenceSku,
          name: item.name,
          price: item.price ?? item.pricing ?? 0,
          quantity: value.quantity ?? 1,
        };

        if (value.subPreferences?.length) {
          output.subSelections = value.subPreferences
            .map((subPref) => {
              const subGroup = (item.subPreferences || []).find(
                (g) =>
                  String(g.preferenceId) ===
                  String(subPref.preferenceId)
              );

              if (!subGroup) return null;

              return {
                title: subGroup.type,
                selection: buildSelection(
                  subGroup,
                  subPref.values || []
                ),
              };
            })
            .filter(Boolean);
        }

        return output;
      })
      .filter(Boolean);
  };

  return preferences
    .map((pref) => {
      const rootGroup = fullPreferenceTree.find(
        (g) =>
          String(g.preferenceId) === String(pref.preferenceId)
      );

      if (!rootGroup) return null;

      return {
        title: rootGroup.type,
        selection: buildSelection(
          rootGroup,
          pref.values || []
        ),
      };
    })
    .filter(Boolean);
};


export const convertCustomisationToPreferenceDetail = (
  customisationPreferences,
  fullPreferenceTree
) => {
  const result = [];

  const groupTypeToId = {};
  const walkTree = (group) => {
    groupTypeToId[group.type] = group.preferenceId;
    (group.preferenceValues || []).forEach((item) => {
      (item.subPreferences || []).forEach((subGroup) => walkTree(subGroup));
    });
  };
  fullPreferenceTree.forEach((rootGroup) => walkTree(rootGroup));

  const walk = (groups, depth = 0, parentSku = null) => {
    (groups || []).forEach((group) => {
      const detail = {
        title: group.title,
        preferenceId: groupTypeToId[group.title] || null,
        selection: [],
      };

      (group.selection || []).forEach((item) => {
        detail.selection.push({
          preferenceSku: item.preferenceSku,
          name: item.name,
          price: item.price ?? item.pricing ?? 0,
          preferenceDietCategory: item.preferenceDietCategory || "",
          isActive: item.isActive ?? true,
          preferenceOrder: item.preferenceOrder || "",
          isSubPreferenceSelected: depth > 0,
          parentPreferenceSku: parentSku,
          quantity: item.quantity ?? 1,
          _id: item.preferenceSku,
        });

        if (item.subSelections?.length) {
          walk(item.subSelections, depth + 1, item.preferenceSku);
        }
      });

      if (detail.selection.length > 0) {
        result.push(detail);
      }
    });
  };

  walk(customisationPreferences);
  return result;
};

export const transformCustomisationPreferences = (input = []) => {
  const cleanSelection = (selection = []) =>
    selection
      .map((item) => {
        const cleaned = {
          name: item.name,
          preferenceSku: item.preferenceSku,
          price: item.price,
          quantity: item.quantity ?? 1
        };

        const subs = cleanSubSelections(item.subSelections);

        if (subs.length > 0) {
          cleaned.subSelections = subs;
        }

        return cleaned;
      })
      .filter(Boolean);

  const cleanSubSelections = (subs = []) =>
    Array.isArray(subs)
      ? subs
          .map((s) => {
            const selection = cleanSelection(s.selection);

            if (!selection.length) return null;

            return {
              title: s.title,
              selection,
            };
          })
          .filter(Boolean)
      : [];

  return input.map((group) => ({
    title: group.title,
    selection: cleanSelection(group.selection),
  }));
};